import { createContext, useContext } from 'react'
import type { AccountService } from '@/application/account/AccountService'
import { DefaultAccountService } from '@/application/account/DefaultAccountService'
import type { AffiliateService } from '@/application/affiliate/AffiliateService'
import { DefaultAffiliateService } from '@/application/affiliate/DefaultAffiliateService'
import type { AuthService } from '@/application/auth/AuthService'
import { DefaultAuthService } from '@/application/auth/DefaultAuthService'
import { DefaultPerformanceService } from '@/application/performance/DefaultPerformanceService'
import type { PerformanceService } from '@/application/performance/PerformanceService'
import { DefaultSessionService } from '@/application/session/DefaultSessionService'
import type { SessionService } from '@/application/session/SessionService'
import type { QRCodeService } from '@/domain/affiliate/QRCodeService'
import type { ClipboardService } from '@/domain/shared/ClipboardService'
import type { ShareService } from '@/domain/shared/ShareService'
import { HttpAccountRepository } from '@/infrastructure/account/HttpAccountRepository'
import { BrowserQRCodeService } from '@/infrastructure/affiliate/BrowserQRCodeService'
import { HttpAffiliateRepository } from '@/infrastructure/affiliate/HttpAffiliateRepository'
import { HttpAuthRepository } from '@/infrastructure/auth/HttpAuthRepository'
import type { HttpClient } from '@/infrastructure/interfaces/HttpClient'
import { HttpPerformanceRepository } from '@/infrastructure/performance/HttpPerformanceRepository'
import { BrowserClipboardService } from '@/infrastructure/storage/BrowserClipboardService'
import { BrowserShareService } from '@/infrastructure/storage/BrowserShareService'

/**
 * 依赖注入容器接口
 */
export interface DIContainer {
  authService: AuthService
  accountService: AccountService
  affiliateService: AffiliateService
  performanceService: PerformanceService
  sessionService: SessionService
  qrCodeService: QRCodeService
  clipboardService: ClipboardService
  shareService: ShareService
}

/**
 * 创建依赖注入容器
 * 该函数负责组装所有的 Repository 和 Service，形成完整的对象图
 */
export function createDIContainer(params: {
  httpClient: HttpClient
  storage: Storage
}): DIContainer {
  const { httpClient, storage } = params

  const sessionService = new DefaultSessionService(storage)

  const authRepository = new HttpAuthRepository(httpClient)
  const accountRepository = new HttpAccountRepository(httpClient)
  const affiliateRepository = new HttpAffiliateRepository(httpClient)
  const performanceRepository = new HttpPerformanceRepository(httpClient)
  const qrCodeService = new BrowserQRCodeService()
  const clipboardService = new BrowserClipboardService()
  const shareService = new BrowserShareService()

  const authService = new DefaultAuthService(
    authRepository,
    accountRepository,
    sessionService,
  )
  const accountService = new DefaultAccountService(accountRepository)
  const affiliateService = new DefaultAffiliateService(
    affiliateRepository,
    performanceRepository,
  )
  const performanceService = new DefaultPerformanceService(
    performanceRepository,
  )

  return {
    authService,
    accountService,
    affiliateService,
    performanceService,
    sessionService,
    qrCodeService,
    clipboardService,
    shareService,
  }
}

/**
 * React Context 用于在组件树中分发 DI 容器
 */
const DIContext = createContext<DIContainer | null>(null)

export const DIProvider = DIContext.Provider

/**
 * Hook: 获取 DI 容器
 */
export function useDI(): DIContainer {
  const container = useContext(DIContext)
  if (!container) {
    throw new Error('useDI must be used within a DIProvider')
  }
  return container
}
